import React, { useState, useEffect, useRef, useCallback } from "react";

import { useServerApi } from "../hooks/userServerApi";
import _ from "lodash";
import moment from "moment";
import { useUserFriendlyDate } from "../hooks/useUserFriendlyDate";
import { useFormatter } from "../hooks/useFomatter";
import {
  Timeline,
  Text,
  Avatar,
  TextInput,
  Group,
  ActionIcon,
  Button,
  Stack,
  Badge,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons";
import ReactJson from "react-json-view";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 20;

const ActivityLog = ({
  form,
  title,
  commentOnly,
  scrollHeight = 300,
  readonly = false,
  ...props
}) => {
  const commentRef = useRef(null);
  const scrollRef = useRef(null);
  const [formatter] = useFormatter();

  const [api] = useServerApi();
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [userFriendlyDate] = useUserFriendlyDate();

  const auth = useAuthUser();

  const entityId = _.get(form.values, "_id");

  const handleCommentKeyDown = async (e) => {
    if (e.key !== "Enter" || !commentRef.current?.value?.trim()) return;
    const result = await api.addCommentLog({
      entityId,
      title: commentRef.current.value.trim(),
      by: auth().id,
    });
    if (result?.success) {
      fetchLog(true);
      commentRef.current.value = "";
    }
  };

  const fetchLog = async (reset = true) => {
    if (!entityId) return;
    const targetPage = reset ? 1 : page;
    if (!reset) setLoadingMore(true);

    const result = await api.getActivityLog(entityId, commentOnly, targetPage, PAGE_SIZE);
    if (!result) {
      if (!reset) setLoadingMore(false);
      return;
    }

    const docs = result.docs ?? [];
    const total = result.total ?? 0;

    if (reset) {
      setLogs(docs);
      setPage(1);
      setHasMore(docs.length < total);
    } else {
      setLogs((prev) => {
        const merged = [...prev, ...docs];
        setHasMore(merged.length < total);
        return merged;
      });
      setLoadingMore(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !entityId) return;
    const nextPage = page + 1;
    setPage(nextPage);
    setLoadingMore(true);

    const result = await api.getActivityLog(entityId, commentOnly, nextPage, PAGE_SIZE);
    if (!result) { setLoadingMore(false); return; }

    const docs = result.docs ?? [];
    const total = result.total ?? 0;
    setLogs((prev) => {
      const merged = [...prev, ...docs];
      setHasMore(merged.length < total);
      return merged;
    });
    setLoadingMore(false);
  }, [loadingMore, hasMore, entityId, page, commentOnly]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) loadMore();
  }, [loadMore]);

  useEffect(() => {
    fetchLog(true);
  }, [entityId]);

  const removeLog = async (log) => {
    if (!log._id) return;
    let result = await api.removeById({
      apiEntity: "activityLog",
      id: log._id,
    });
    if (result.success) {
      fetchLog(true);
    }
  };

  const getByName = (by) => (by ? formatter.friendlyName(by) : "SYSTEM");

  const getLogTitle = (log) => {
    return log.type != "COMMENT"
      ? `${log.title} by ${getByName(log.by)} `
      : `${log.title}`;
  };

  const [t] = useTranslation();

  return (
    <>
      {/* {auth().id} */}
      {/* entityId:{entityId} */}
      {/* <ReactJson src={auth()} style={{ background: "white" }} collapsed /> */}
      <Group mb={10} position="apart" noWrap>
        {!readonly ? (
          <TextInput
            ref={commentRef}
            onKeyDown={handleCommentKeyDown}
            placeholder={t("Add comment... (Enter to save)")}
            size="xs"
            style={{ flex: 1 }}
          />
        ) : <span />}
        <Button onClick={() => fetchLog(true)} variant="default" size="xs">
          {t("Refresh")}
        </Button>
      </Group>
      {!_.isEmpty(logs) && (
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{ height: scrollHeight, overflowY: "auto", marginTop: 24 }}
        >
          <Timeline active={logs.length} lineWidth={2} bulletSize={10} ml="md">
            {logs.map((log, index) => (
              <Timeline.Item
                key={index}
                title={<Text size="xs" weight={600}>{getLogTitle(log)}</Text>}
                bulletSize={22}
                bullet={
                  <Avatar
                    src={
                      log.by && log.by.image
                        ? `${process.env.REACT_APP_FILE_URL}/${log.by?.image}`
                        : null
                    }
                    size="xs"
                    radius="xl"
                  />
                }
              >
                <Group>
                  <Text color="dimmed" size="xs">
                    {` ${userFriendlyDate(log.createdAt, "LLL")} by ${getByName(
                      log.by
                    )}`}
                  </Text>

                  {log.type === "COMMENT" && auth().id === log.by._id && (
                    <ActionIcon
                      size={15}
                      color="gray"
                      onClick={() => removeLog(log)}
                    >
                      <IconTrash />
                    </ActionIcon>
                  )}
                </Group>

                {log.description ? (
                  <Text size="xs" mt={4} style={{ whiteSpace: "pre-wrap" }}>
                    {log.description}
                  </Text>
                ) : null}

                {log.changes && log.changes.length > 0 && (
                  <Stack spacing={2} mt={6}>
                    {log.changes.map((change, i) => {
                      const tooltipLabel = change.oldValue
                        ? `${change.oldValue}  →  ${change.newValue}`
                        : change.newValue;
                      return (
                        <Tooltip
                          key={i}
                          label={tooltipLabel}
                          multiline
                          width={320}
                          withArrow
                          position="bottom-start"
                          styles={{ tooltip: { fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-word" } }}
                        >
                          <Group spacing={6} noWrap style={{ cursor: "default", maxWidth: "100%" }}>
                            <Badge size="xs" variant="outline" color="gray" style={{ minWidth: 90, flexShrink: 0 }}>
                              {change.fieldLabel}
                            </Badge>
                            <Text
                              size="xs"
                              color="dimmed"
                              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}
                            >
                              {change.oldValue ? (
                                <>
                                  <Text component="span" size="xs" strikethrough color="red.4">
                                    {change.oldValue}
                                  </Text>
                                  {" → "}
                                  <Text component="span" size="xs" color="teal.4" weight={500}>
                                    {change.newValue}
                                  </Text>
                                </>
                              ) : (
                                <Text component="span" size="xs" color="teal.4" weight={500}>
                                  {change.newValue}
                                </Text>
                              )}
                            </Text>
                          </Group>
                        </Tooltip>
                      );
                    })}
                  </Stack>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
          {loadingMore && (
            <Center mt="sm" mb="xs">
              <Loader size="xs" />
            </Center>
          )}
          {!hasMore && logs.length >= PAGE_SIZE && (
            <Center mt="sm" mb="xs">
              <Text size="xs" color="dimmed">All logs loaded</Text>
            </Center>
          )}
        </div>
      )}
    </>
  );
};

export default ActivityLog;
